import React, { useState } from 'react';

/**
 * HelpCenterPage — admin operations manual with sidebar tabs.
 */

const TABS = [
  { id: 'getting-started', label: '시작하기', icon: 'play' },
  { id: 'menu', label: '메뉴 관리', icon: 'book' },
  { id: 'orders', label: '주문 관리', icon: 'list' },
  { id: 'payment', label: '결제/정산', icon: 'card' },
  { id: 'qr', label: '테이블 / QR', icon: 'grid' },
  { id: 'staff-call', label: '직원 호출', icon: 'bell' },
  { id: 'faq', label: '자주 묻는 질문', icon: 'help' },
];

export default function HelpCenterPage() {
  const [active, setActive] = useState('getting-started');

  return (
    <div className="page-container" style={{ padding: '32px 40px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px', marginBottom: 6 }}>
          운영 매뉴얼
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>
          스마트오더 관리자 기능을 단계별로 안내합니다.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24, alignItems: 'flex-start' }}>
        {/* Sidebar */}
        <aside
          style={{
            background: 'var(--surface)',
            borderRadius: 16,
            padding: 12,
            boxShadow: 'var(--shadow-sm)',
            position: 'sticky',
            top: 20,
          }}
        >
          {TABS.map((t) => {
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActive(t.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 14px',
                  background: isActive ? 'var(--primary-light)' : 'transparent',
                  color: isActive ? 'var(--primary)' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: 10,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: isActive ? 700 : 500,
                  textAlign: 'left',
                  marginBottom: 2,
                }}
              >
                <TabIcon name={t.icon} />
                {t.label}
              </button>
            );
          })}
        </aside>

        {/* Content */}
        <main
          style={{
            background: 'var(--surface)',
            borderRadius: 16,
            padding: 32,
            boxShadow: 'var(--shadow-sm)',
            minHeight: 400,
          }}
        >
          {active === 'getting-started' && <GettingStarted />}
          {active === 'menu' && <MenuGuide />}
          {active === 'orders' && <OrdersGuide />}
          {active === 'payment' && <PaymentGuide />}
          {active === 'qr' && <QrGuide />}
          {active === 'staff-call' && <StaffCallGuide />}
          {active === 'faq' && <FaqGuide />}
        </main>
      </div>
    </div>
  );
}

/* ==================== Reusable bits ==================== */

function Heading({ children }) {
  return (
    <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 8, letterSpacing: '-0.4px' }}>
      {children}
    </h2>
  );
}

function Sub({ children }) {
  return (
    <p style={{ fontSize: 14, color: 'var(--text-tertiary)', lineHeight: 1.7, marginBottom: 20 }}>
      {children}
    </p>
  );
}

function SectionTitle({ children }) {
  return (
    <h3
      style={{
        fontSize: 16,
        fontWeight: 800,
        color: 'var(--text)',
        marginTop: 24,
        marginBottom: 10,
        paddingBottom: 8,
        borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
    </h3>
  );
}

function Steps({ items }) {
  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: '12px 0' }}>
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            display: 'flex',
            gap: 14,
            marginBottom: 12,
            padding: '14px 16px',
            background: 'var(--bg)',
            borderRadius: 10,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'var(--primary)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {i + 1}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{item}</div>
        </li>
      ))}
    </ol>
  );
}

function Screenshot({ label }) {
  return (
    <div
      aria-label={`${label} 스크린샷`}
      style={{
        marginTop: 12,
        marginBottom: 16,
        padding: '40px 20px',
        background: 'var(--border)',
        borderRadius: 12,
        textAlign: 'center',
        color: 'var(--text-quaternary)',
        fontSize: 13,
        fontWeight: 600,
        border: '2px dashed var(--divider)',
      }}
    >
      {label} · 스크린샷 예정
    </div>
  );
}

function Tip({ children, type = 'info' }) {
  const palette = {
    info: { bg: 'var(--info-light, #E0F2FE)', fg: 'var(--info, #0EA5E9)' },
    warning: { bg: 'var(--warning-light, #FFF5E0)', fg: 'var(--warning, #FF9500)' },
    success: { bg: 'var(--success-light, #E8FAE8)', fg: 'var(--success, #00BF40)' },
  }[type];
  return (
    <div
      style={{
        padding: '14px 16px',
        background: palette.bg,
        color: palette.fg,
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.7,
        marginTop: 12,
        marginBottom: 12,
      }}
    >
      <strong style={{ marginRight: 6 }}>TIP.</strong>
      {children}
    </div>
  );
}

/* ==================== Sections ==================== */

function GettingStarted() {
  return (
    <div>
      <Heading>시작하기</Heading>
      <Sub>첫 매장 설정부터 QR 부착까지, 가장 먼저 해야 할 5가지 단계를 안내합니다.</Sub>

      <SectionTitle>첫 매장 설정</SectionTitle>
      <Steps
        items={[
          '회원가입 후 자동 노출되는 온보딩 화면에서 매장 기본 정보를 입력합니다.',
          '사업자등록번호, 상호, 대표자 등 법적 정보를 등록합니다.',
          '정산 계좌 정보를 입력합니다. 예금주는 사업자등록증 명의와 일치해야 합니다.',
          '결제 모드(후결제/선결제)와 직원 호출 사용 여부를 선택합니다.',
          '서비스 이용약관에 동의하면 설정이 완료됩니다.',
        ]}
      />
      <Screenshot label="온보딩 진행 화면" />

      <SectionTitle>초기 메뉴 등록</SectionTitle>
      <Sub>대시보드 진입 후 좌측 사이드바의 "메뉴 관리"에서 첫 메뉴를 등록해 보세요.</Sub>
      <Tip>메뉴 이미지가 있는 경우 주문 전환율이 평균 1.7배 더 높습니다.</Tip>

      <SectionTitle>테이블 생성 및 QR 부착</SectionTitle>
      <Sub>"테이블" 메뉴에서 테이블 번호를 등록하고 QR 코드를 출력해 테이블에 부착합니다.</Sub>
      <Tip type="warning">QR 코드는 방수 코팅하여 부착하시면 오랜 기간 사용할 수 있습니다.</Tip>
    </div>
  );
}

function MenuGuide() {
  return (
    <div>
      <Heading>메뉴 관리</Heading>
      <Sub>카테고리, 메뉴, 옵션, 품절 처리, 인기 메뉴 설정 방법을 설명합니다.</Sub>

      <SectionTitle>카테고리 등록</SectionTitle>
      <Steps
        items={[
          '메뉴 관리 화면에서 "카테고리 추가" 버튼을 누릅니다.',
          '카테고리 이름과 표시 순서를 입력합니다.',
          '저장하면 고객 화면 상단 탭에 즉시 반영됩니다.',
        ]}
      />

      <SectionTitle>메뉴 등록</SectionTitle>
      <Steps
        items={[
          '카테고리를 선택한 뒤 "메뉴 추가"를 누릅니다.',
          '메뉴명, 가격, 설명, 이미지를 입력합니다.',
          '필요 시 옵션(추가토핑, 맵기 등)을 등록합니다.',
          '저장 후 고객 화면에 즉시 반영됩니다.',
        ]}
      />
      <Screenshot label="메뉴 등록 화면" />

      <SectionTitle>옵션 설정</SectionTitle>
      <Sub>메뉴마다 다중 선택 가능한 옵션을 등록할 수 있습니다. 각 옵션은 추가 가격 설정이 가능합니다.</Sub>
      <Tip>옵션 가격이 0원이면 "무료"로 표시되며, 고객이 자유롭게 선택할 수 있는 필수/선택 여부 또한 지정할 수 있습니다.</Tip>

      <SectionTitle>품절 처리</SectionTitle>
      <Sub>메뉴 카드의 품절 토글을 이용해 즉시 품절 여부를 변경할 수 있습니다. 품절된 메뉴는 고객 화면에서 선택이 제한됩니다.</Sub>

      <SectionTitle>인기 메뉴 설정</SectionTitle>
      <Sub>메뉴 편집 화면에서 "인기 메뉴"로 지정하면 고객 화면 상단의 추천 섹션에 노출됩니다.</Sub>
    </div>
  );
}

function OrdersGuide() {
  return (
    <div>
      <Heading>주문 관리</Heading>
      <Sub>주문 접수부터 서빙, 취소까지의 전체 흐름을 안내합니다.</Sub>

      <SectionTitle>주문 접수</SectionTitle>
      <Steps
        items={[
          '"주문" 메뉴에 실시간으로 신규 주문이 표시됩니다.',
          '소리 알림이 울리며, 상단 배지에 미처리 건수가 표시됩니다.',
          '주문 카드의 "확인" 버튼을 눌러 접수합니다.',
        ]}
      />

      <SectionTitle>조리 및 서빙</SectionTitle>
      <Steps
        items={[
          '접수된 주문은 "조리 중" 상태로 전환됩니다.',
          '조리가 완료되면 "서빙 완료" 버튼을 누릅니다.',
          '고객의 주문 상태 화면에도 실시간으로 반영됩니다.',
        ]}
      />
      <Screenshot label="주문 카드 흐름" />

      <SectionTitle>주문 취소</SectionTitle>
      <Sub>조리가 시작되기 전까지 주문을 취소할 수 있습니다. 취소 사유는 정산 리포트에 기록됩니다.</Sub>
      <Tip type="warning">이미 결제가 완료된 주문을 취소하면 자동으로 환불 절차가 시작됩니다.</Tip>
    </div>
  );
}

function PaymentGuide() {
  return (
    <div>
      <Heading>결제 / 정산</Heading>
      <Sub>결제 모드 변경과 정산 주기, 입금일을 확인하세요.</Sub>

      <SectionTitle>결제 모드</SectionTitle>
      <Sub>후결제 모드는 식사 후 결제, 선결제 모드는 주문 동시 결제입니다. 설정 화면에서 언제든 변경할 수 있습니다.</Sub>

      <SectionTitle>정산 주기 및 입금일</SectionTitle>
      <Steps
        items={[
          '정산 주기는 주간(매주 수요일) 입금을 기본으로 합니다.',
          '월~일요일 매출이 그 주 수요일 오전에 집계됩니다.',
          '수수료(카드 수수료 + 플랫폼 이용료)를 공제한 뒤 등록 계좌로 송금됩니다.',
          '공휴일이 포함된 경우 다음 영업일로 이월됩니다.',
        ]}
      />
      <Tip>세금계산서는 매월 말일 기준으로 발행되며 관리자 이메일로 자동 전송됩니다.</Tip>
    </div>
  );
}

function QrGuide() {
  return (
    <div>
      <Heading>테이블 / QR</Heading>
      <Sub>테이블 생성, QR 출력, 부착까지 단계별로 안내합니다.</Sub>

      <SectionTitle>테이블 생성</SectionTitle>
      <Steps
        items={[
          '"테이블" 메뉴에서 "테이블 추가"를 누릅니다.',
          '테이블 번호와 좌석 수를 입력합니다.',
          '저장하면 해당 테이블 전용 QR 코드가 즉시 생성됩니다.',
        ]}
      />

      <SectionTitle>QR 출력</SectionTitle>
      <Sub>테이블 목록에서 "QR 출력" 버튼을 누르면 인쇄 가능한 PDF가 생성됩니다. A5 스티커 용지에 인쇄하시면 편리합니다.</Sub>
      <Screenshot label="QR 인쇄 미리보기" />

      <SectionTitle>부착 가이드</SectionTitle>
      <Tip>테이블 중앙에 눕힌 형태 혹은 테이블 모서리에 세운 형태로 부착하세요. 고객이 자연스럽게 볼 수 있는 위치가 가장 좋습니다.</Tip>
      <Tip type="warning">방수 코팅을 권장합니다. 국물이나 음료에 닿으면 QR 인식률이 떨어질 수 있습니다.</Tip>
    </div>
  );
}

function StaffCallGuide() {
  return (
    <div>
      <Heading>직원 호출</Heading>
      <Sub>고객이 QR 화면에서 직원을 부르면 관리자 화면에 실시간 알림이 도착합니다.</Sub>

      <SectionTitle>사용 방법</SectionTitle>
      <Steps
        items={[
          '사이드바의 "직원 호출" 메뉴에 접속합니다.',
          '미처리 호출이 상단에 표시됩니다. 호출 사유(물, 수저 등)와 테이블 번호를 확인합니다.',
          '처리 완료 후 "완료" 버튼을 눌러 목록에서 제거합니다.',
        ]}
      />

      <SectionTitle>알림 설정</SectionTitle>
      <Sub>설정 화면에서 호출 수신 시 소리와 진동 알림을 켜고 끌 수 있습니다. 태블릿 상시 켜짐 모드를 권장합니다.</Sub>
      <Tip>직원 호출이 30초 이상 응대되지 않으면 자동으로 재알림이 울립니다.</Tip>
    </div>
  );
}

function FaqGuide() {
  const items = [
    { q: '주문이 접수되지 않아요.', a: '주문 화면 상단의 실시간 연결 상태를 확인하세요. 연결이 끊긴 경우 새로고침하면 복구됩니다.' },
    { q: 'QR 코드를 다시 인쇄하려면?', a: '테이블 메뉴에서 해당 테이블의 "QR 출력" 버튼을 눌러 재인쇄할 수 있습니다.' },
    { q: '메뉴 사진이 고객 화면에 흐릿하게 보여요.', a: '가로 800px 이상의 고화질 이미지(1:1 비율 권장)를 업로드하면 선명하게 표시됩니다.' },
    { q: '결제 취소는 어떻게 하나요?', a: '주문 상세 화면의 "환불" 버튼을 누르면 자동으로 승인 취소가 이루어집니다.' },
    { q: '세금계산서는 어디서 받나요?', a: '매월 1일에 등록된 이메일로 자동 발송됩니다. 수동 발행은 고객센터에 요청해 주세요.' },
    { q: '여러 매장을 하나의 계정으로 관리할 수 있나요?', a: '가능합니다. 우상단 매장 전환 메뉴에서 새 매장을 추가하실 수 있습니다.' },
    { q: '데이터는 어떻게 보관되나요?', a: '모든 데이터는 AWS 서울 리전에 암호화되어 저장되며, 접근 기록이 로그로 남습니다.' },
    { q: '직원 계정을 따로 만들 수 있나요?', a: '네. 설정 > 계정 관리에서 직원용 계정을 추가할 수 있으며 권한 제한이 가능합니다.' },
    { q: '프린터와 연동되나요?', a: '영수증 프린터(ESC/POS 호환)와 블루투스 또는 LAN으로 연동 가능합니다. 자세한 내용은 고객센터에 문의해 주세요.' },
    { q: '수수료는 얼마인가요?', a: '카드 결제 수수료 2.5% + 플랫폼 이용료 0.5%입니다. 자세한 내용은 요금제 페이지를 참고해 주세요.' },
  ];
  return (
    <div>
      <Heading>자주 묻는 질문</Heading>
      <Sub>운영 중 자주 발생하는 질문 10가지를 모았습니다.</Sub>
      <div style={{ marginTop: 12 }}>
        {items.map((it, i) => (
          <div
            key={i}
            style={{
              padding: '16px 18px',
              background: 'var(--bg)',
              borderRadius: 12,
              marginBottom: 10,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
              Q. {it.q}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.75 }}>
              A. {it.a}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==================== Icons ==================== */

function TabIcon({ name }) {
  const props = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'play':
      return (
        <svg {...props}>
          <polygon points="5 3 19 12 5 21 5 3" />
        </svg>
      );
    case 'book':
      return (
        <svg {...props}>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case 'list':
      return (
        <svg {...props}>
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      );
    case 'card':
      return (
        <svg {...props}>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
      );
    case 'grid':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      );
    case 'bell':
      return (
        <svg {...props}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
    case 'help':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    default:
      return null;
  }
}
